import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { http, errorText } from "@/lib/api";
import { groupBy } from "@/components/site/PriceList";
import { AdminTitle } from "./AdminLayout";

const Row = ({ item, onSaved, onDelete }) => {
  const [r, setR] = useState(item);
  const dirty = JSON.stringify(r) !== JSON.stringify(item);
  const save = async () => {
    try {
      const { id, updated_at, ...body } = r;
      await http.put(`/admin/services/${id}`, body);
      toast.success("Lagret");
      onSaved();
    } catch (e) { toast.error(errorText(e)); }
  };
  return (
    <div className="grid grid-cols-12 gap-3 items-center py-3 border-b border-[#E5E0D8]" data-testid={`admin-price-row-${item.id}`}>
      <Input value={r.name} onChange={(e) => setR({ ...r, name: e.target.value })} className="col-span-12 md:col-span-4 rounded-none h-9" data-testid={`price-name-${item.id}`} />
      <p className="col-span-6 md:col-span-2 text-xs text-[#5C5A56]">Gammel: {r.old_price || "–"}</p>
      <Input value={r.price} placeholder="Ny pris, f.eks. fra 450 kr" onChange={(e) => setR({ ...r, price: e.target.value })} className="col-span-6 md:col-span-3 rounded-none h-9" data-testid={`price-new-${item.id}`} />
      <label className="col-span-6 md:col-span-2 flex items-center gap-2 text-xs">
        <Switch checked={r.approved} onCheckedChange={(v) => setR({ ...r, approved: v })} data-testid={`price-approved-${item.id}`} />
        {r.approved ? "Godkjent" : "Utkast"}
      </label>
      <div className="col-span-6 md:col-span-1 flex justify-end gap-1">
        {dirty && <button onClick={save} className="px-3 py-1.5 bg-[#1A1A1A] text-white text-xs" data-testid={`price-save-${item.id}`}>Lagre</button>}
        <button onClick={() => onDelete(item.id)} className="p-1.5 text-[#5C5A56] hover:text-red-700" data-testid={`price-delete-${item.id}`}><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
      </div>
    </div>
  );
};

export default function PricesAdmin() {
  const [items, setItems] = useState([]);
  const [onlyDraft, setOnlyDraft] = useState(false);
  const [nw, setNw] = useState({ category: "", name: "" });
  const load = () => http.get("/admin/services").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!nw.category || !nw.name) return toast.error("Fyll inn kategori og navn");
    await http.post("/admin/services", { ...nw, order: items.length });
    setNw({ category: "", name: "" });
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Slette prisen?")) return;
    await http.delete(`/admin/services/${id}`);
    load();
  };

  const proposals = items.filter((i) => !i.approved && i.price).length;
  const approveAll = async () => {
    if (!window.confirm(`Godkjenne alle ${proposals} prisforslag? De vises da på nettsiden.`)) return;
    const { data } = await http.post("/admin/services/approve-proposals");
    toast.success(`${data.approved} priser godkjent`);
    load();
  };
  const shown = onlyDraft ? items.filter((i) => !i.approved) : items;
  const groups = groupBy(shown, "category");
  return (
    <div data-testid="admin-prices">
      <AdminTitle title="Priser høsten 2026" sub="Gamle priser er hentet som utkast. Skriv ny pris og slå på «Godkjent» – først da vises prisen på nettsiden. Ellers vises «Pris etter vurdering». AI-assistenten bruker kun godkjente priser.">
        {proposals > 0 && <button onClick={approveAll} className="px-5 py-2.5 bg-[#1A1A1A] text-white text-sm" data-testid="price-approve-all-btn">Godkjenn alle {proposals} forslag</button>}
        <label className="flex items-center gap-2 text-sm"><Switch checked={onlyDraft} onCheckedChange={setOnlyDraft} data-testid="price-filter-draft" />Kun utkast</label>
      </AdminTitle>
      {Object.entries(groups).map(([cat, rows]) => (
        <section key={cat} className="mb-10">
          <h2 className="text-2xl font-medium mb-2">{cat}</h2>
          {rows.map((r) => <Row key={`${r.id}-${r.updated_at}`} item={r} onSaved={load} onDelete={remove} />)}
        </section>
      ))}
      <div className="mt-10 p-6 bg-[#F0EBE1] grid md:grid-cols-12 gap-3" data-testid="price-add-form">
        <Input placeholder="Kategori (f.eks. Bukser)" value={nw.category} onChange={(e) => setNw({ ...nw, category: e.target.value })} className="md:col-span-4 rounded-none bg-white" data-testid="price-add-category" />
        <Input placeholder="Tjeneste" value={nw.name} onChange={(e) => setNw({ ...nw, name: e.target.value })} className="md:col-span-6 rounded-none bg-white" data-testid="price-add-name" />
        <button onClick={add} className="md:col-span-2 flex items-center justify-center gap-2 bg-[#1A1A1A] text-white text-sm py-2" data-testid="price-add-btn"><Plus className="w-4 h-4" />Legg til</button>
      </div>
    </div>
  );
}
